/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Lightweight loader + helpers around the Google Maps JavaScript SDK.
 *
 * The SDK is injected once (singleton promise) with the Places library.
 * The client-side Maps JS key lives in VUE_APP_GOOGLE_MAPS_KEY and MUST be
 * restricted by HTTP-referrer in the Google Cloud Console.
 *
 * Distance is a reference value only — it is computed on the client and sent
 * to the backend as-is (see LMS-1236). Nothing here validates or bills.
 */

export interface GooglePlace {
  placeId: string;
  address: string;
  name: string;
  lat: number | null;
  lng: number | null;
}

export interface DrivingDistance {
  /** Distance in meters (Google's canonical `distance.value`). */
  meters: number;
  /** Human-readable distance, e.g. "123 km". */
  text: string;
  /** Travel duration in seconds. */
  durationSeconds: number;
}

/** A point can be resolved either by Google place id (preferred) or a raw address. */
export interface DistancePoint {
  placeId?: string | null;
  address?: string | null;
}

let loaderPromise: Promise<any> | null = null;

/**
 * Load the Google Maps SDK (with the Places library) exactly once.
 * Resolves with the global `google` object.
 */
export function loadGoogleMaps(): Promise<any> {
  if (loaderPromise) {
    return loaderPromise;
  }

  loaderPromise = new Promise<any>((resolve, reject) => {
    const w = window as any;

    if (w.google && w.google.maps && w.google.maps.places) {
      resolve(w.google);
      return;
    }

    const key = process.env.VUE_APP_GOOGLE_MAPS_KEY;
    if (!key) {
      loaderPromise = null;
      reject(new Error("VUE_APP_GOOGLE_MAPS_KEY is not set"));
      return;
    }

    const callbackName = "__lmsInitGoogleMaps__";
    w[callbackName] = () => resolve(w.google);

    const script = document.createElement("script");
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}` +
      `&libraries=places&v=weekly&loading=async&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      loaderPromise = null;
      reject(new Error("Failed to load the Google Maps SDK"));
    };
    document.head.appendChild(script);
  });

  return loaderPromise;
}

function mapLegacyPlace(result: any): GooglePlace {
  const location = result.geometry && result.geometry.location;
  return {
    placeId: result.place_id || "",
    address: result.formatted_address || result.name || "",
    name: result.name || "",
    lat: location ? location.lat() : null,
    lng: location ? location.lng() : null
  };
}

let legacyPlacesService: any = null;

/**
 * Free-text place search via the Places `textSearch` API enabled for the LMS
 * Google Cloud project.
 * Returns an empty array on any non-OK status (including ZERO_RESULTS).
 */
export async function searchPlaces(query: string): Promise<GooglePlace[]> {
  const trimmed = (query || "").trim();
  if (!trimmed) {
    return [];
  }

  const google = await loadGoogleMaps();
  if (!legacyPlacesService) {
    legacyPlacesService = new google.maps.places.PlacesService(
      document.createElement("div")
    );
  }
  return new Promise<GooglePlace[]>(resolve => {
    legacyPlacesService.textSearch(
      { query: trimmed },
      (results: any, status: any) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          resolve(results.slice(0, 7).map(mapLegacyPlace));
        } else {
          resolve([]);
        }
      }
    );
  });
}

/** Legacy search results already include the canonical address and coordinates. */
export async function resolvePlace(
  selection: GooglePlace
): Promise<GooglePlace> {
  return selection;
}

function toMatrixValue(google: any, point: DistancePoint): any {
  if (point.placeId) {
    return { placeId: point.placeId };
  }
  return point.address || "";
}

/**
 * Driving distance between two points via the Distance Matrix API.
 * Resolves `null` when the route cannot be computed.
 */
export async function getDrivingDistance(
  origin: DistancePoint,
  destination: DistancePoint
): Promise<DrivingDistance | null> {
  const hasOrigin = origin && (origin.placeId || origin.address);
  const hasDestination =
    destination && (destination.placeId || destination.address);
  if (!hasOrigin || !hasDestination) {
    return null;
  }

  const google = await loadGoogleMaps();
  const service = new google.maps.DistanceMatrixService();

  return new Promise<DrivingDistance | null>(resolve => {
    service.getDistanceMatrix(
      {
        origins: [toMatrixValue(google, origin)],
        destinations: [toMatrixValue(google, destination)],
        travelMode: google.maps.TravelMode.DRIVING
      },
      (response: any, status: any) => {
        if (status !== "OK" || !response) {
          resolve(null);
          return;
        }
        const element =
          response.rows &&
          response.rows[0] &&
          response.rows[0].elements &&
          response.rows[0].elements[0];
        if (!element || element.status !== "OK") {
          resolve(null);
          return;
        }
        resolve({
          meters: element.distance.value,
          text: element.distance.text,
          durationSeconds: element.duration ? element.duration.value : 0
        });
      }
    );
  });
}

/** Format meters as kilometers with two decimals, e.g. 1590 -> "1.59 km". */
export function metersToKm(meters: number | null | undefined): string {
  if (meters === null || meters === undefined) {
    return "";
  }
  return `${(meters / 1000).toFixed(2)} km`;
}
